import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import databaseService from '../services/databaseService';
import { isAuthenticated } from '../services/authService';
import RichTextEditor from './RichTextEditor';
import './Journal.css';

function Journal() {
  const navigate = useNavigate();
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/auth/login');
      return;
    }
    
    loadJournalEntries();
  }, [navigate]);

  const loadJournalEntries = async () => {
    try {
      await databaseService.init();
      const entries = await databaseService.getAllJournalEntries();
      setJournalEntries(entries);
    } catch (error) {
      console.error('Failed to load journal entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntry = (entry) => {
    navigate(`/admin/edit/journal/${entry.id}`);
  };

  const handleDeleteEntry = async (id) => {
    if (window.confirm('Are you sure you want to delete this journal entry?')) {
      try {
        await databaseService.deleteJournalEntry(id);
        loadJournalEntries();
      } catch (error) {
        console.error('Failed to delete journal entry:', error);
      }
    }
  };

  const handleSaveEntry = async (entryData) => {
    try {
      if (editingEntry) {
        await databaseService.updateJournalEntry(editingEntry.id, {
          ...editingEntry,
          ...entryData,
          updated_at: new Date().toISOString()
        });
      } else {
        await databaseService.createJournalEntry({
          ...entryData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      }
      
      setEditingEntry(null);
      setShowNewEntry(false);
      loadJournalEntries();
    } catch (error) {
      console.error('Failed to save journal entry:', error);
    }
  };

  const handleCancel = () => {
    setEditingEntry(null);
    setShowNewEntry(false);
  };

  // Pagination logic
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = journalEntries.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(journalEntries.length / entriesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <div className="journal-container">Loading journal entries...</div>;

  if (showNewEntry || editingEntry) {
    return (
      <div className="journal-container">
        <RichTextEditor
          initialContent={editingEntry?.content || ''}
          title={editingEntry?.title || ''}
          onSave={handleSaveEntry}
          onCancel={handleCancel}
          isEditing={!!editingEntry}
        />
      </div>
    );
  }

  return (
    <div className="journal-container">
      <div className="journal-header">
        <h1>Journal</h1>
        <button 
          onClick={() => setShowNewEntry(true)}
          className="btn btn-primary"
        >
          + New Entry
        </button>
      </div>

      <div className="journal-stats">
        <p>Total entries: {journalEntries.length}</p>
      </div>

      <div className="journal-entries">
        {currentEntries.length > 0 ? (
          currentEntries.map((entry, index) => (
            <div key={entry.id || index} className="journal-entry">
              <div className="entry-content">
                <h3>{entry.title}</h3>
                <p className="entry-excerpt">{entry.excerpt}</p>
                <div className="entry-meta">
                  <span>Created: {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : 'Unknown'}</span>
                  {entry.updated_at && entry.updated_at !== entry.created_at && 
                    <span> | Updated: {new Date(entry.updated_at).toLocaleDateString()}</span>
                  }
                </div>
              </div>
              <div className="entry-actions">
                <button 
                  onClick={() => handleEditEntry(entry)} 
                  className="btn btn-small btn-primary"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteEntry(entry.id)} 
                  className="btn btn-small btn-danger"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-entries">
            <p>No journal entries yet. Create your first entry!</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          
          <div className="pagination-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`btn ${currentPage === number ? 'btn-primary' : 'btn-secondary'}`}
              >
                {number}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Journal;
